/* global __WEB__ */

import map from 'lodash/map';
import without from 'lodash/without';
import guid from './guid';
import throttler from './throttler';

import { stringify } from 'query-string';

class NoticeSubscriber {
    constructor() {
        this._promise = new Promise((resolve) => {
            this._resolve = resolve;
        });

        // Keep a reference to each symbol we subscribe to by channel-id
        //  [channel-id]: [symbol]
        this.channels = {};

        this.subscribingSymbols = [];

        this.hasSent = false;
    }

    start(dispatch, action, url) {
        this.url = url;

        // Create a dispatch function which is throttled, to be called maximun once per 750 ms
        // per ticker.
        this.dispatch = throttler((...args) => {
            dispatch(action(...args));
        }, 750);

        this.connect();
    }

    ready() {
        return this._promise;
    }

    connect() {
        if (!window.WebSocket) {
            return;
        }

        this.ws = new WebSocket(this.url);
        this.ws.onmessage = event => {
            // Ignored messages
            if (
                event.data === 'pong' ||
                event.data === 'ack_subscribe' ||
                event.data === 'ack_unsubscribe'
            ) {
                return;
            }

            try {
                const data = JSON.parse(event.data);

                if (typeof data.type === 'undefined') {
                    return;
                }

                if (data.type === 'new' || data.type === 'update') {
                    this.dispatch(data.key, data.values);
                }
            } catch (e) {
                // ignore error
            }
        };

        this.ws.onopen = () => {
            // Subscribe to any channels that were previously open, in case we've been
            // disconnected and are reconnected.
            if (this.hasSent) {
                this.resubscribeChannels();
            }

            this.pingInterval = setInterval(() => this.send('ping'), 1000 * 30);

            this._resolve(this.ws);
        };

        this.ws.onclose = () => {
            clearInterval(this.pingInterval);
            setTimeout(() => this.connect(), 5000);
        };
    }

    resubscribeChannels() {
        const symbolsToResubscribe = [];

        // Go trough and unsubscribe to each channel first, to properly close things
        // on OMS's end.
        for (const channel in this.channels) {
            if (this.channels.hasOwnProperty(channel)) {
                const symbol = this.channels[channel];
                symbolsToResubscribe.push(symbol);

                this.unsubscribe(channel);

                // Delete the channel, so we don't resubscribe to it again.
                delete this.channels[channel];
            }
        }

        // Re-subscribe to each symbol.
        symbolsToResubscribe.forEach(symbol => this.subscribe(symbol));
    }

    send(message) {
        this.ready().then(ws => {
            if (ws.readyState === WebSocket.OPEN) {
                this.hasSent = true;
                ws.send(message);
            }
        });
    }

    query(params) {
        const channel = guid();

        const query = stringify({
            ...params,
            channel,
        }, { arrayFormat: 'index' });

        this.send(`subscribe?${query}`);

        return channel;
    }

    subscribe(symbol) {
        const ticker = symbol || 'All';

        // Avoid subscribing if we are already subscribing to it
        if (this.subscribingSymbols.indexOf(ticker) > -1) {
            return null;
        }

        const channel = guid();

        const filter = symbol ? `COMPANY_TICKER%%s^(.*,)?${ticker}(,.*)?$` : 'ITEM==sAll';

        const query = map({
            columns: [
                'TIME',
                'REFERENCEID',
                'COMPANY_TICKER',
                'NEWSTYPE',
                'HEADLINE',
                'TEXT',
            ].join(','),
            type: 'history',
            source: 'feed.news.ob.ALL',
            filter,
            start: new Date().setHours(0, 0, 0, 0),
            limit: 10,
            channel,
        }, (value, key) => `${key}=${value}`).join('&');

        // Store a reference to the channel. So we can re-subscribe if we lose connection.
        this.channels[channel] = ticker;
        this.subscribingSymbols.push(ticker);

        this.send(`subscribe?${query}`);

        return channel;
    }

    unsubscribe(channel) {
        if (!channel) {
            return;
        }

        // Clean up, so we can subscribe to the symbol again
        if (this.channels.hasOwnProperty(channel)) {
            const symbol = this.channels[channel];
            this.symbols = without(this.symbols, symbol);
            delete this.channels[channel];
        }

        this.send(`unsubscribe?channel=${channel}`);
    }
}

export default __WEB__ ? new NoticeSubscriber() : null;



/** WEBPACK FOOTER **
 ** /src/shared/service/noticesubscriber.js
 **/